import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setWireImageAC } from "actions-thunks/actions";
import { accountThunks } from "actions-thunks/account-thunks";
import UploadButton from "../edit-profile/UploadButton";
import { utilityFunctions } from "../utilities/utilityFunctions";

import "./wire-confirmation-modal.scss";

const WireConfirmationModal = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { currentAccount } = useSelector((state) => state.accounts);
  const { wireImage } = useSelector((state) => state.accounts);
  const MAX_WIRE_AMOUNT = 100000000;
  const [amount, setAmount] = useState("");
  const [newImageUploaded, setNewImageUploaded] = useState(false);
  const [errors, setErrors] = useState({});

  const isPDF = wireImage?.image?.split("/")[1]?.split(";")[0] === "pdf";

  const validateAmount = (value) => {
    if (!value || value.trim() === "") {
      return "Please enter an amount";
    }
    const numValue = parseFloat(value.replace(/[^0-9.]/g, ""));
    if (isNaN(numValue) || numValue <= 0) {
      return "Please enter a valid amount";
    }
    if (numValue > MAX_WIRE_AMOUNT) {
      return "Maximum wire amount is $100,000,000";
    }
    return "";
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    if (value && value.trim() !== "") {
      const error = validateAmount(value);
      setErrors((prev) => ({ ...prev, amount: error }));
    } else {
      setErrors((prev => ({ ...prev, amount: "" })));
    }
  };

  const handleAmountBlur = (e) => {
    const value = e.target.value;
    if (value) {
      const numValue = parseFloat(value.replace(/[^0-9.]/g, ""));
      if (!isNaN(numValue) && numValue > 0) {
        // Format as currency on blur
        const formatted = utilityFunctions.currencyFormatter().format(numValue);
        setAmount(formatted);
      }
    }
  };

  const handleConfirm = async () => {
    // Validate amount
    const amountError = validateAmount(amount);
    if (amountError) {
      setErrors((prev) => ({ ...prev, amount: amountError }));
      return;
    }

    try {
      // Extract numeric value from amount string
      const numericAmount = parseFloat(amount.replace(/[^0-9.]/g, ""));

      const wireData = {
        amount: numericAmount,
        status: "claimed",
        ...(wireImage && { confirmation: wireImage }),
      };

      await dispatch(accountThunks.createWireTC(currentAccount._id, wireData));
      
      // Clear wire image after successful creation
      dispatch(setWireImageAC(null));
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Error creating wire:", error);
    }
  };

  const handleCancel = () => {
    dispatch(setWireImageAC(null));
    setAmount("");
    setErrors({});
    onClose();
  };

  const handleRemoveImage = () => {
    dispatch(setWireImageAC(null));
    setNewImageUploaded(false);
  };

  const handleChangeImage = () => {
    const foundButton = document.getElementById("main-upload-image-button");
    if (foundButton) {
      foundButton.click();
    }
  };

  return (
    <div className="wire-confirmation-modal">
      <div className="black-block" onClick={handleCancel} />
      <div className="wire-dialog-block">
        <header>Wire Confirmation</header>
        <div className="wire-dialog">
          <span>Do you confirm that a wire was sent?</span>
          <hr />
          
          <div className="amount-field">
            <label htmlFor="amount-sent">Amount sent</label>
            <input
              id="amount-sent"
              type="text"
              value={amount}
              onChange={handleAmountChange}
              onBlur={handleAmountBlur}
              placeholder="$0.00"
              className={errors.amount ? "error" : ""}
            />
            {errors.amount && (
              <span className="error-message">{errors.amount}</span>
            )}
          </div>

          {wireImage && (
            <div className="wire-image-block">
              <img
                className="wire-image pad-btn"
                src={
                  isPDF
                    ? `${process.env.PUBLIC_URL}/pdf-icon.png`
                    : wireImage.image
                }
                alt="wire confirmation"
              />
              <div className="wire-image-buttons">
                <button
                  className="wire-button upload-wire-button"
                  onClick={handleRemoveImage}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/close-red.png`}
                    alt="close"
                  />
                  Remove
                </button>
                <button
                  className="wire-button upload-wire-button"
                  onClick={handleChangeImage}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/open-folder.png`}
                    alt="open"
                  />
                  Change
                </button>
              </div>
            </div>
          )}

          <UploadButton
            show={!wireImage}
            setNewImageUploaded={setNewImageUploaded}
            actionType="fee"
            setPopupState={() => {}}
          />
        </div>
        <div className="wire-dialog-bottom-buttons fixed-button-size">
          <button
            id="wire-confirm-button"
            className="wire-button"
            onClick={handleConfirm}
            disabled={!amount || !!errors.amount}
          >
            Yes, confirm
          </button>
          <button
            id="wire-cancel-button"
            className="wire-button wire-button-transparent"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default WireConfirmationModal;
